import { STORAGE_KEYS, localeTexts, questionBank } from './crmQuizContent.js';

export { STORAGE_KEYS, localeTexts, questionBank };

export function getLocale() {
	const locale = window.CRMQuizSettings?.locale || document.documentElement.lang || 'ua';
	const normalized = String(locale).toLowerCase().replace('_', '-');
	const baseLocale = normalized.split('-')[0];
	const alias = baseLocale === 'uk' ? 'ua' : baseLocale;
	return Object.prototype.hasOwnProperty.call(localeTexts, normalized)
		? normalized
		: Object.prototype.hasOwnProperty.call(localeTexts, alias)
			? alias
			: 'ua';
}

export function getQuestionText(question, locale) {
	return question.text[locale] || question.text.en;
}

export function getOptionText(option, locale) {
	return option.text[locale] || option.text.en;
}

export function getDefaultAnswers() {
	return questionBank.map((question) => (question.multiple ? [] : null));
}

export function calculateResult(answers) {
	let smartScore = 0;
	let d365Score = 0;

	questionBank.forEach((question, questionIndex) => {
		const answer = answers[questionIndex];

		if (question.multiple) {
			(answer || []).forEach((optionIndex) => {
				const option = question.options[optionIndex];
				if (option) {
					smartScore += option.smart;
					d365Score += option.d365;
				}
			});
			return;
		}

		if (answer !== null && answer !== undefined) {
			const option = question.options[answer];
			if (option) {
				smartScore += option.smart;
				d365Score += option.d365;
			}
		}
	});

	const total = smartScore + d365Score || 1;
	const smartPct = Math.round((smartScore / total) * 100);
	const d365Pct = 100 - smartPct;
	const isSmartWinner = smartScore >= d365Score;
	const isTie = smartScore === d365Score;
	const diff = Math.abs(smartScore - d365Score);

	return {
		smartScore,
		d365Score,
		smartPct,
		d365Pct,
		isSmartWinner,
		isTie,
		confidence: diff <= 4 ? 'slight' : diff <= 10 ? 'moderate' : 'clear',
		recommendationKey: isTie ? 'tie' : isSmartWinner ? 'smart' : 'd365',
		recommendationTitle: isTie ? 'Consult a specialist' : isSmartWinner ? 'SMART CRM' : 'Microsoft Dynamics 365',
	};
}

export function buildSummary(answers, locale) {
	return questionBank.map((question, questionIndex) => {
		const answer = answers[questionIndex];
		let answerText = '';

		if (question.multiple) {
			answerText = (answer || [])
				.map((optionIndex) => {
					if (optionIndex && typeof optionIndex === 'object') {
						return optionIndex.text || optionIndex.label || optionIndex.value || '';
					}

					const normalizedIndex = Number(optionIndex);
					const option = Number.isFinite(normalizedIndex) ? question.options[normalizedIndex] : null;
					return option ? getOptionText(option, locale) : '';
				})
				.filter(Boolean)
				.join(', ');
		} else if (answer !== null && answer !== undefined) {
			if (typeof answer === 'object') {
				answerText = answer.text || answer.label || answer.value || '';
			} else {
				const normalizedIndex = Number(answer);
				const option = Number.isFinite(normalizedIndex) ? question.options[normalizedIndex] : null;
				answerText = option ? getOptionText(option, locale) : String(answer || '').trim();
			}
		}

		const option = question.multiple ? null : question.options[answer];
		const diff = question.multiple || !option ? 0 : option.smart - option.d365;

		return {
			question: getQuestionText(question, locale),
			answer: answerText,
			diff: question.multiple ? (answer || []).length : diff,
		};
	});
}

export function buildSummaryDescription(summary) {
	return summary
		.map((item, index) => {
			const question = String(item?.question || item?.questionText || item?.label || `Question ${index + 1}`).trim();
			const rawAnswer = item?.answer ?? item?.answerText ?? item?.selectedAnswer ?? item?.selected ?? item?.value ?? item?.label ?? item?.text ?? '';
			const answer = Array.isArray(rawAnswer)
				? rawAnswer
					.map((entry) => String(entry?.text || entry?.label || entry?.value || entry || '').trim())
					.filter(Boolean)
					.join(', ')
				: String(rawAnswer || '').trim();

			return `${question}: ${answer || '-'}`;
		})
		.join('\n');
}

export function getStoredSession() {
	try {
		const sessionStored = window.sessionStorage.getItem(STORAGE_KEYS.session);
		if (sessionStored) {
			return JSON.parse(sessionStored);
		}

		const draftStored = window.localStorage.getItem(STORAGE_KEYS.draft);
		if (draftStored) {
			return JSON.parse(draftStored);
		}

		const legacyStored = window.localStorage.getItem(STORAGE_KEYS.session);
		return legacyStored ? JSON.parse(legacyStored) : null;
	} catch (error) {
		return null;
	}
}

export function getStoredLead() {
	return null;
}

export function saveSession(payload) {
	const serialized = JSON.stringify(payload);
	window.sessionStorage.setItem(STORAGE_KEYS.session, serialized);
	window.localStorage.setItem(STORAGE_KEYS.draft, serialized);
	window.localStorage.setItem(STORAGE_KEYS.session, serialized);
}

export function saveResultLink(payload) {
	window.CRMQuizLeadContext = payload;
	window.CRMQuizResultContext = payload;
	window.CRMQuizResultUrl = payload.resultUrl || '';
}

export function openPopupById(popupId, updateHash = true) {
	const normalizedId = popupId.startsWith('#') ? popupId : `#${popupId}`;
	const popup = document.querySelector(normalizedId);
	const popupBg = document.querySelector('.popup-bg');

	if (!popup || !popupBg) {
		return false;
	}

	document.querySelectorAll('.popup').forEach((item) => item.classList.remove('show'));
	popup.classList.add('show');
	popupBg.classList.add('show');
	document.body.classList.add('scroll-disable');
	document.body.style.paddingRight = `${window.innerWidth - document.documentElement.clientWidth}px`;

	if (updateHash) {
		window.location.hash = normalizedId;
	}

	return true;
}
